import React, { useState } from "react";
import { Button, Input, Select, Tooltip, Upload } from "antd";
import { EditOutlined, LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

/**
 * WhatsApp-style mockup of the family_invitation_v1 invitation (Social Gathering events).
 *
 * It mirrors the approved Meta template line by line:
 *   image -> "حضرة {title} / {guest}" -> greeting -> invitation sentence -> event title -> date ->
 *   welcome line -> instruction line -> closing line + 🌹 -> three buttons.
 *
 * With `editable`, the lines the organiser may change (guest title, invitation sentence, event title,
 * closing line, image) can be edited right here. The component holds no state of its own about those values:
 * the parent passes the current values in and gets every change back through the on* callbacks, which write
 * to the same form fields as the inputs above the preview - so the two always show the same thing.
 * Lines marked with a lock are fixed by the approved template. The date and the guest name are filled in
 * automatically and are not editable here. Buttons are visual only.
 */
const COPY = {
  guestPrefix: "حضرة",
  guestNameSample: "اسم الضيف",
  salam: "السلام عليكم ورحمة الله وبركاته،",
  dateLabel: "الموعد:",
  joy: "ويسعدنا تشريفكم ومشاركتكم هذه المناسبة السعيدة.",
  pressButton: "يرجى الضغط على الزر أدناه لتأكيد حضوركم واستلام بطاقة الدعوة الخاصة بكم.",
  accept: "سأحضر",
  reject: "لن أتمكن من الحضور",
  download: "تحميل الدعوة",
  imagePlaceholder: "ستظهر صورة الدعوة هنا",
  titlePlaceholder: "سيظهر عنوان الفعالية هنا",
  datePlaceholder: "سيظهر موعد الفعالية هنا",
};

const paragraph = { margin: "0 0 10px" };
const editableStyle = {
  borderBottom: "1px dashed #128c7e",
  background: "rgba(18,140,126,0.07)",
  borderRadius: 3,
  padding: "0 3px",
  cursor: "text",
};

// A line of text that turns into a one-line input when clicked. Changes are sent up on every keystroke.
const InlineText = ({ value, placeholder, maxLength, editable, onChange, fallback, hint }) => {
  const [editing, setEditing] = useState(false);

  if (!editable) return <span>{value || placeholder}</span>;

  if (editing) {
    return (
      <Input
        autoFocus
        size="small"
        dir="rtl"
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value.replace(/\s+/g, " "))}
        onBlur={() => {
          // Never leave a line empty: an emptied line goes back to its default wording.
          if (!String(value || "").trim() && fallback) onChange(fallback);
          setEditing(false);
        }}
        onPressEnter={() => setEditing(false)}
        style={{ direction: "rtl", fontSize: 13 }}
      />
    );
  }

  return (
    <Tooltip title={hint}>
      <span
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") setEditing(true);
        }}
        style={editableStyle}
      >
        {value || placeholder} <EditOutlined style={{ fontSize: 10, color: "#128c7e" }} />
      </span>
    </Tooltip>
  );
};

// The guest title: shown as text, a small dropdown when clicked.
const InlineSelect = ({ value, options, editable, onChange, hint }) => {
  const [editing, setEditing] = useState(false);

  if (!editable) return <span>{value}</span>;

  if (editing) {
    return (
      <Select
        autoFocus
        defaultOpen
        size="small"
        value={value}
        options={options}
        popupMatchSelectWidth={false}
        onChange={(next) => {
          onChange(next);
          setEditing(false);
        }}
        onBlur={() => setEditing(false)}
        style={{ minWidth: 130 }}
      />
    );
  }

  return (
    <Tooltip title={hint}>
      <span role="button" tabIndex={0} onClick={() => setEditing(true)} style={{ ...editableStyle, cursor: "pointer" }}>
        {value} <EditOutlined style={{ fontSize: 10, color: "#128c7e" }} />
      </span>
    </Tooltip>
  );
};

const ButtonRow = ({ icon, label, last }) => (
  <div
    style={{
      padding: "9px 12px",
      textAlign: "center",
      fontSize: 13,
      color: "#128c7e",
      borderBottom: last ? "none" : "1px solid #eee",
      cursor: "default",
    }}
  >
    <span style={{ marginInlineEnd: 6 }}>{icon}</span>
    {label}
  </div>
);

const WhatsAppInvitationPreview = ({
  editable = false,
  titleEditable = true,
  displayTitle,
  guestTitle,
  guestTitleOptions = [],
  invitationText,
  closingText,
  defaultInvitationText,
  defaultClosingText,
  eventDate,
  invitationImageUrl,
  onChangeGuestTitle,
  onChangeInvitationText,
  onChangeTitle,
  onChangeClosingText,
  onImageUpload,
  onEditDate,
}) => {
  const { t } = useTranslation();
  const editHint = t("eventForm.whatsapp.previewClickToEdit");

  // A fixed line: plain text with a small lock that explains why it cannot be edited.
  const Fixed = ({ children }) => (
    <p style={paragraph}>
      {children}{" "}
      <Tooltip title={t("eventForm.whatsapp.previewFixedTooltip")}>
        <LockOutlined style={{ fontSize: 10, color: "#aaa" }} />
      </Tooltip>
    </p>
  );

  const image = (
    <div
      style={{
        width: "100%",
        height: 160,
        backgroundColor: "#d9d9d9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        cursor: editable && onImageUpload ? "pointer" : "default",
      }}
    >
      {invitationImageUrl ? (
        <img src={invitationImageUrl} alt="Invitation" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <span style={{ fontSize: 12, color: "#888", padding: 8, textAlign: "center" }}>{COPY.imagePlaceholder}</span>
      )}
    </div>
  );

  return (
    <div>
      {/* antd wraps the upload trigger in an inline-block, which shrinks the image; make it fill the bubble. */}
      <style>{`.wa-preview-upload, .wa-preview-upload .ant-upload { display: block !important; width: 100%; }`}</style>
      <div
        style={{
          maxWidth: 340,
          width: "100%",
          margin: "0 auto",
          borderRadius: 24,
          border: "8px solid #1f1f1f",
          backgroundColor: "#e5ddd5",
          overflow: "hidden",
          boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
        }}
      >
        {/* WhatsApp-style header */}
        <div
          style={{
            backgroundColor: "#075e54",
            color: "#fff",
            padding: "10px 12px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            direction: "ltr",
          }}
        >
          <span style={{ fontSize: 18 }}>&larr;</span>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: "#128c7e",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
            }}
          >
            ✉
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>Future Cards</div>
          <span style={{ fontSize: 16 }}>&#8942;</span>
        </div>

        {/* Message area */}
        <div style={{ padding: 14, minHeight: 260 }}>
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 8,
              overflow: "hidden",
              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
              direction: "rtl",
              textAlign: "right",
            }}
          >
            {editable && onImageUpload ? (
              <Upload
                accept="image/*"
                showUploadList={false}
                customRequest={onImageUpload}
                className="wa-preview-upload"
                style={{ display: "block", width: "100%" }}
              >
                <Tooltip title={editHint}>{image}</Tooltip>
              </Upload>
            ) : (
              image
            )}

            <div style={{ padding: "10px 12px 4px", fontSize: 13, lineHeight: 1.8, color: "#111" }}>
              {/* {{1}} guest title + name (the name is filled in per guest when the message is sent) */}
              <p style={paragraph}>
                {COPY.guestPrefix}{" "}
                <InlineSelect
                  value={guestTitle}
                  options={guestTitleOptions}
                  editable={editable}
                  onChange={onChangeGuestTitle}
                  hint={editHint}
                />{" "}
                / {COPY.guestNameSample}
              </p>

              <Fixed>{COPY.salam}</Fixed>

              {/* {{2}} invitation sentence */}
              <p style={paragraph}>
                <InlineText
                  value={invitationText}
                  fallback={defaultInvitationText}
                  maxLength={200}
                  editable={editable}
                  onChange={onChangeInvitationText}
                  hint={editHint}
                />
              </p>

              {/* {{3}} event title */}
              <p style={paragraph}>
                <InlineText
                  value={displayTitle}
                  placeholder={COPY.titlePlaceholder}
                  maxLength={150}
                  editable={editable && titleEditable}
                  onChange={onChangeTitle}
                  hint={editHint}
                />
              </p>

              {/* {{4}} date - filled in from the event, changed in the Date & Venue step */}
              <p style={paragraph}>
                📅 {COPY.dateLabel} {eventDate || COPY.datePlaceholder}
                {editable && onEditDate && (
                  <>
                    {" "}
                    <Button type="link" size="small" onClick={onEditDate} style={{ fontSize: 11, padding: 0, height: "auto" }}>
                      {t("eventForm.whatsapp.previewChangeDate")}
                    </Button>
                  </>
                )}
              </p>

              <Fixed>{COPY.joy}</Fixed>
              <Fixed>{COPY.pressButton}</Fixed>

              {/* {{5}} closing line */}
              <p style={{ ...paragraph, marginBottom: 2 }}>
                <InlineText
                  value={closingText}
                  fallback={defaultClosingText}
                  maxLength={200}
                  editable={editable}
                  onChange={onChangeClosingText}
                  hint={editHint}
                />{" "}
                🌹
              </p>
              <div style={{ textAlign: "left", direction: "ltr", fontSize: 11, color: "#667781", paddingBottom: 6 }}>
                10:32
              </div>
            </div>

            <div style={{ borderTop: "1px solid #eee" }}>
              <ButtonRow icon="↩" label={COPY.accept} />
              <ButtonRow icon="↩" label={COPY.reject} />
              <ButtonRow icon="⧉" label={COPY.download} last />
            </div>
          </div>
        </div>
      </div>

      {editable && (
        <div className="text-xs text-gray-500 mt-3">{t("eventForm.whatsapp.previewEditHint")}</div>
      )}
    </div>
  );
};

export default WhatsAppInvitationPreview;
